import { usePlayerActions } from "@utils/player/core/usePlayer";
import LArtwork from "@components/LArtwork";
import LBox from "@components/LBox";
import LIcon from "@components/LIcon";
import LPressable from "@components/LPressable";
import LText from "@components/LText";
import { OptionsGridSWM } from "@components/OptionsMenuModal/OptionsGridSWM";
import QueueFadeMask from "@components/OptionsMenuModal/QueueFadeMask";
import { QueueListSWM } from "@components/OptionsMenuModal/QueueTabSWM";
import { OptionsMenuModalProps } from "@components/OptionsMenuModal/types";
import { SetListToggleSWM } from "@components/QueueManager/SongSWM";
import { getActiveTab } from "@hooks/useActiveTab";
import { useActiveSong, type CatalogSong } from "@utils/player";
import { useTrackIndividualOfflineStatus } from "@utils/downloadManager/hooks";
import { BottomSheetPlainLayout } from "@utils/swmModal/components/BottomSheetPrimitives";
import AppModalSheet from "@utils/appModal/components/AppModalSheet";
import { router } from "expo-router";
import {
  Fragment,
  forwardRef,
  memo,
  useCallback,
  useRef,
  useState,
} from "react";
import { Dimensions } from "react-native";
import { SheetAdapterRef } from "react-native-bottom-sheet-stack";

const QUEUE_VIEWPORT_HEIGHT = Dimensions.get("window").height * 0.3;
const DOCK_OVERLAY_HEIGHT = 72;

const SongDisplay = memo(function SongDisplay({
  activeSetListExpanded,
  closeModal,
  onOpenOverflow,
  onToggleActiveSetList,
  song,
}: {
  activeSetListExpanded: boolean;
  closeModal: () => void;
  onOpenOverflow: (layout: {
    x: number;
    y: number;
    width: number;
    height: number;
  }) => void;
  onToggleActiveSetList?: () => void;
  song: CatalogSong;
}) {
  const status = useTrackIndividualOfflineStatus(song.id);
  const overflowButtonRef = useRef<any>(null);

  const openPlayer = useCallback(() => {
    closeModal();
    router.navigate({
      pathname: `/(app)/(tabs)/${getActiveTab()}`,
    });
  }, [closeModal]);

  const openArtist = useCallback(
    (artistId: string) => {
      closeModal();
      router.navigate({
        pathname: `/(app)/(tabs)/${getActiveTab()}/artist/[artistId]`,
        params: { artistId, collectionType: "artist" },
      });
    },
    [closeModal],
  );

  return (
    <LBox
      alignItems="center"
      borderBottomColor="blackTranslucent10"
      borderBottomWidth={1}
      flexDirection="row"
      gap="s3"
      pb="s3"
    >
      <LPressable sentryEventName="open_music_player_swm" onPress={openPlayer}>
        <LArtwork
          borderRadius="m"
          imageSize="small"
          size={52}
          url={song.albums.at(0)?.image}
          showTracklistChip={song.isSetList}
        />
      </LPressable>
      <LBox justifyContent="space-between" flex={1} gap="s1">
        <LText color="black" numberOfLines={1} variant="bodyLBold">
          {song.title}
        </LText>
        <LBox flexDirection="row" alignItems="center" gap="s1">
          {status === "downloaded" ? (
            <LIcon name="downloaded" size={12} />
          ) : null}
          <LText
            accessible={false}
            color="blackTranslucent70"
            flexShrink={1}
            numberOfLines={1}
            variant="bodyM2m"
          >
            {song.artists.map((artist, index) => (
              <Fragment key={artist.id}>
                {index > 0 ? ", " : null}
                <LText
                  accessibilityRole="link"
                  accessible
                  color="blackTranslucent70"
                  onPress={() => openArtist(artist.id)}
                  sentryEventName="options_menu_artist_press"
                  variant="bodyM2m"
                >
                  {artist.name}
                </LText>
              </Fragment>
            ))}
          </LText>
        </LBox>
      </LBox>
      {onToggleActiveSetList ? (
        <SetListToggleSWM
          expanded={activeSetListExpanded}
          onPress={onToggleActiveSetList}
        />
      ) : null}
      <LBox ref={overflowButtonRef} collapsable={false}>
        <LPressable
          hitSlop={12}
          onPress={() => {
            overflowButtonRef.current?.measure(
              (
                _x: number,
                _y: number,
                width: number,
                height: number,
                pageX: number,
                pageY: number,
              ) => {
                onOpenOverflow({ x: pageX, y: pageY, width, height });
              },
            );
          }}
          sentryEventName="options_menu_more_actions"
        >
          <LBox
            alignItems="center"
            backgroundColor="blackTranslucent10"
            borderRadius="max"
            height={40}
            justifyContent="center"
            width={40}
          >
            <LIcon colors={{ color: "black" }} name="ellipsis" size={22} />
          </LBox>
        </LPressable>
      </LBox>
    </LBox>
  );
});

const OptionsMenuModalSWMContent = ({
  activeSong: initialActiveSong,
  closeModal,
  selectedCollection,
  selectedCatalogSong,
}: OptionsMenuModalProps & { closeModal: () => void }) => {
  const playerActions = usePlayerActions();

  const [activeSetListExpanded, setActiveSetListExpanded] = useState(false);
  const [showOverflow, setShowOverflow] = useState(false);
  const [selectedSongQueueIds, setSelectedSongQueueIds] = useState<string[]>(
    [],
  );
  const [overflowButtonLayout, setOverflowButtonLayout] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const activeSong = useActiveSong() ?? initialActiveSong;
  const catalogSong: CatalogSong | undefined =
    selectedCatalogSong ?? activeSong;
  const displayedSongIsActiveSetList = Boolean(
    catalogSong &&
    activeSong?.id === catalogSong.id &&
    catalogSong.isSetList &&
    catalogSong.setList?.entries?.length,
  );

  const selectSongQueueId = useCallback((songQueueId: string) => {
    setSelectedSongQueueIds((previousSongQueueIds) =>
      previousSongQueueIds.includes(songQueueId)
        ? previousSongQueueIds.filter(
            (previousSongQueueId) => previousSongQueueId !== songQueueId,
          )
        : [...previousSongQueueIds, songQueueId],
    );
  }, []);

  const removeSelectedSongs = useCallback(() => {
    playerActions.removeFromQueue(selectedSongQueueIds);
    setSelectedSongQueueIds([]);
  }, [selectedSongQueueIds]);

  if (!catalogSong) {
    return null;
  }

  return (
    <BottomSheetPlainLayout contentProps={{ px: "zero" }}>
      <LBox px="s4">
        <SongDisplay
          activeSetListExpanded={activeSetListExpanded}
          closeModal={closeModal}
          onOpenOverflow={(layout) => {
            setOverflowButtonLayout(layout);
            setShowOverflow(true);
          }}
          onToggleActiveSetList={
            displayedSongIsActiveSetList
              ? () => setActiveSetListExpanded((expanded) => !expanded)
              : undefined
          }
          song={catalogSong}
        />
      </LBox>
      <LBox
        height={(activeSong ? QUEUE_VIEWPORT_HEIGHT : 0) + DOCK_OVERLAY_HEIGHT}
        position="relative"
        pt="s3"
      >
        {activeSong ? (
          <QueueFadeMask>
            <LBox flex={1} px="s4">
              <QueueListSWM
                activeSetListExpanded={activeSetListExpanded}
                closeModal={closeModal}
                selectedSongQueueIds={selectedSongQueueIds}
                selectSongQueueId={selectSongQueueId}
              />
            </LBox>
          </QueueFadeMask>
        ) : null}
        <LBox
          bottom={0}
          left={0}
          pointerEvents="box-none"
          position="absolute"
          right={0}
        >
          <LBox px="s4">
            <OptionsGridSWM
              closeMenu={closeModal}
              closeOverflow={() => setShowOverflow(false)}
              overflowButtonLayout={overflowButtonLayout}
              onRemoveFromQueue={removeSelectedSongs}
              selectedCollection={selectedCollection}
              selectedQueueSongCount={selectedSongQueueIds.length}
              showOverflow={showOverflow}
              song={catalogSong}
            />
          </LBox>
        </LBox>
      </LBox>
    </BottomSheetPlainLayout>
  );
};

export const OptionsMenuModalSWMSheet = forwardRef<
  SheetAdapterRef,
  OptionsMenuModalProps
>(function OptionsMenuModalSWMSheet(props, ref) {
  return (
    <AppModalSheet ref={ref} showHandle={false}>
      {({ closeModal }) => (
        <OptionsMenuModalSWMContent {...props} closeModal={closeModal} />
      )}
    </AppModalSheet>
  );
});

export default OptionsMenuModalSWMSheet;
